import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from '../../shared/Modal';
import {
  Reservation,
  ModificationType,
  ModificationPreview,
  ModifyResult,
  AvailableSite,
  SiteOccupancy,
} from '../../../types';
import { ApiReservation, getReservationById } from '../../../services/reservationService';
import managerApi from '../../../services/auth/managerApi';
import { FeeAdjustmentFields, DiscountCodeField, AppliedFeeLines } from '../shared/AdminFeeControls';
import {
  FeeAdjustmentState,
  EMPTY_FEE_ADJUSTMENT,
  isAdjustmentIncomplete,
  buildFeeAdjustmentPayload,
  extractAppliedDiscountCode,
} from '../shared/feeAdjustmentUtils';
import './ModifyReservationWizard.css';

interface ModifyReservationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  onSuccess?: () => void;
}

type Step = 'type' | 'edit' | 'preview' | 'confirm' | 'result';

const STEPS: { key: Step; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'edit', label: 'Edit' },
  { key: 'preview', label: 'Preview' },
  { key: 'confirm', label: 'Confirm' },
  { key: 'result', label: 'Result' },
];

// Guest-name edits never change the price, so they skip the preview/payment steps.
const GUEST_STEPS: { key: Step; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'edit', label: 'Edit' },
  { key: 'confirm', label: 'Confirm' },
  { key: 'result', label: 'Result' },
];

const MODIFICATION_TYPES: { key: ModificationType; label: string; description: string }[] = [
  { key: 'occupancy', label: 'A: Quick Occupancy', description: 'Change number of guests only' },
  { key: 'date', label: 'B: Quick Date Change', description: 'Change dates and/or occupancy' },
  { key: 'site', label: 'C: Quick Site Change', description: 'Move to different site(s)' },
  { key: 'rebook', label: 'D: Full Rebook', description: 'Change everything (sites, dates, occupancy)' },
  { key: 'guests', label: 'E: Guest Names', description: 'Swap or rename who is staying (no cost change)' },
];

// other_guests is a comma-separated string capped at 250 chars in the DB
const OTHER_GUESTS_MAX_LENGTH = 250;

const parseOtherGuests = (value?: string): string[] =>
  (value || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

const ModificationWizard: React.FC<ModifyReservationWizardProps> = ({
  isOpen,
  onClose,
  reservation,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection
  const [modificationType, setModificationType] = useState<ModificationType | null>(null);

  // Full reservation from API (all sites)
  const [fullReservation, setFullReservation] = useState<ApiReservation | null>(null);
  const [loadingFullReservation, setLoadingFullReservation] = useState(false);

  // Form state — shared fields
  const [newCheckIn, setNewCheckIn] = useState('');
  const [newDays, setNewDays] = useState(1);

  // Form state — per-site
  const [siteOccupancies, setSiteOccupancies] = useState<SiteOccupancy[]>([]);
  const [selectedSiteAliases, setSelectedSiteAliases] = useState<string[]>([]);

  // Preview & Result
  const [preview, setPreview] = useState<ModificationPreview | null>(null);
  const [result, setResult] = useState<ModifyResult | null>(null);
  const [availableSites, setAvailableSites] = useState<AvailableSite[]>([]);

  // Configuration
  const [selectedWaiveFees, setSelectedWaiveFees] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  // Payment collection for the additional charge. 'payment_link' emails the guest a
  // link; cash/check/card_terminal record an in-person payment atomically with the
  // modification (backend accepts payment_method/payment_status/terminal_transaction_id).
  const [paymentMethod, setPaymentMethod] =
    useState<'cash' | 'check' | 'card_terminal' | 'payment_link'>('payment_link');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending'>('pending');
  const [terminalTransactionId, setTerminalTransactionId] = useState('');

  // Discount code + fee adjustment
  const [discountCode, setDiscountCode] = useState('');
  const [initialDiscountCode, setInitialDiscountCode] = useState('');
  const discountInitialized = useRef(false);
  const [discountSilentDrop, setDiscountSilentDrop] = useState(false);
  const [feeAdjustment, setFeeAdjustment] = useState<FeeAdjustmentState>(EMPTY_FEE_ADJUSTMENT);

  // Group event flag
  const [isGroupEventReservation, setIsGroupEventReservation] = useState(false);

  // Guest names (type E) — primary guest on the reservation plus the additional
  // guests, edited one row at a time and stored back as a comma-separated string.
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [otherGuestNames, setOtherGuestNames] = useState<string[]>([]);
  const [initialGuests, setInitialGuests] = useState({ first: '', last: '', others: '' });
  const [guestUpdateSuccess, setGuestUpdateSuccess] = useState(false);

  const isGuestEdit = modificationType === 'guests';
  const steps = isGuestEdit ? GUEST_STEPS : STEPS;
  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  // Normalized guest values as they will be sent to the backend
  const otherGuestsValue = otherGuestNames.map((n) => n.trim()).filter(Boolean).join(', ');
  const guestsTooLong = otherGuestsValue.length > OTHER_GUESTS_MAX_LENGTH;
  const guestsChanged =
    guestFirstName.trim() !== initialGuests.first ||
    guestLastName.trim() !== initialGuests.last ||
    otherGuestsValue !== initialGuests.others;

  // Computed totals across all sites
  const totalAdults = useMemo(() => siteOccupancies.reduce((sum, s) => sum + s.adults, 0), [siteOccupancies]);
  const totalChildren = useMemo(() => siteOccupancies.reduce((sum, s) => sum + s.children, 0), [siteOccupancies]);
  const totalPets = useMemo(() => siteOccupancies.reduce((sum, s) => sum + s.pets, 0), [siteOccupancies]);
  const totalVehicles = useMemo(() => siteOccupancies.reduce((sum, s) => sum + s.vehicles, 0), [siteOccupancies]);

  // Helper to update a single site's occupancy
  const updateSiteOccupancy = (index: number, field: keyof Omit<SiteOccupancy, 'site_alias' | 'site_id'>, value: number) => {
    setSiteOccupancies(prev => prev.map((site, i) =>
      i === index ? { ...site, [field]: value } : site
    ));
  };

  // Fetch full reservation on wizard open
  useEffect(() => {
    if (!isOpen || !reservation) return;

    // Reset discount pre-fill tracking for the newly opened reservation
    discountInitialized.current = false;
    setDiscountCode('');
    setInitialDiscountCode('');
    setDiscountSilentDrop(false);
    setFeeAdjustment(EMPTY_FEE_ADJUSTMENT);

    const fetchFull = async () => {
      setLoadingFullReservation(true);
      setError(null);
      try {
        const fullRes = await getReservationById(reservation.id);
        setFullReservation(fullRes);

        const checkIn = fullRes.check_in.split('T')[0];
        setNewCheckIn(checkIn);
        setNewDays(fullRes.days);

        const activeSites = (fullRes.reservation_sites || []).filter(
          (rs) => rs.status === 'active'
        );

        const occupancies: SiteOccupancy[] = activeSites.map((rs) => ({
          site_alias: rs.site?.alias || '',
          site_id: rs.site_id,
          adults: rs.adults,
          children: rs.children,
          pets: rs.pets,
          vehicles: rs.vehicles,
        }));
        setSiteOccupancies(occupancies);
        setSelectedSiteAliases(occupancies.map((o) => o.site_alias));

        const others = parseOtherGuests(fullRes.other_guests);
        setGuestFirstName(fullRes.guest_first_name || '');
        setGuestLastName(fullRes.guest_last_name || '');
        setOtherGuestNames(others);
        setInitialGuests({
          first: (fullRes.guest_first_name || '').trim(),
          last: (fullRes.guest_last_name || '').trim(),
          others: others.join(', '),
        });
      } catch (err: any) {
        setError('Failed to load reservation details: ' + (err.message || 'Unknown error'));
      } finally {
        setLoadingFullReservation(false);
      }
    };

    fetchFull();
  }, [isOpen, reservation]);

  // Load available sites when fullReservation is ready and modification type needs it
  useEffect(() => {
    if (!reservation || !fullReservation || (modificationType !== 'site' && modificationType !== 'rebook')) {
      return;
    }

    const fetchSites = async () => {
      try {
        const params = new URLSearchParams({
          store_id: fullReservation.store_id.toString(),
          check_in: newCheckIn,
          days: newDays.toString(),
          exclude_reservation_id: reservation.id.toString(),
        });
        const data = await managerApi.get(`/reservations/available-sites?${params}`);
        setAvailableSites(data.available_sites || []);
      } catch (err) {
        console.error('Failed to load available sites:', err);
      }
    };

    fetchSites();
  }, [fullReservation, modificationType, reservation, newCheckIn, newDays]);

  const loadPreview = async () => {
    if (!reservation || !modificationType) return;

    setLoading(true);
    setError(null);

    try {
      let endpoint: string;
      let body: Record<string, any>;

      const occupancyPayload = {
        new_adult: totalAdults,
        new_child: totalChildren,
        new_pets: totalPets,
        new_vehicles: totalVehicles,
        site_occupancies: siteOccupancies,
        // Send discount_code only when the admin changed it ("" removes, non-empty applies,
        // omit = carry the existing code forward and re-validate)
        ...(discountCode.trim() !== initialDiscountCode
          ? { discount_code: discountCode.trim() }
          : {}),
        // Only send the adjustment once it has a reason (backend 422s without it)
        ...(isAdjustmentIncomplete(feeAdjustment) ? {} : buildFeeAdjustmentPayload(feeAdjustment)),
      };

      switch (modificationType) {
        case 'occupancy':
          endpoint = `/reservations/${reservation.id}/modify-preview`;
          body = { ...occupancyPayload };
          break;
        case 'date':
          endpoint = `/reservations/${reservation.id}/modify-preview`;
          body = {
            new_check_in: newCheckIn,
            new_days: newDays,
            ...occupancyPayload,
          };
          break;
        case 'site':
          endpoint = `/reservations/${reservation.id}/site-change-preview`;
          body = {
            new_site_aliases: selectedSiteAliases,
            ...occupancyPayload,
            site_occupancies: siteOccupancies.map((s, i) => ({
              ...s,
              site_alias: selectedSiteAliases[i] || s.site_alias,
            })),
          };
          break;
        case 'rebook':
          endpoint = `/reservations/${reservation.id}/rebook-preview`;
          body = {
            new_site_aliases: selectedSiteAliases,
            new_check_in: newCheckIn,
            new_days: newDays,
            ...occupancyPayload,
            site_occupancies: siteOccupancies.map((s, i) => ({
              ...s,
              site_alias: selectedSiteAliases[i] || s.site_alias,
            })),
          };
          break;
        default:
          throw new Error('Invalid modification type');
      }

      const data: ModificationPreview = await managerApi.post(endpoint, body);
      setPreview(data);
      setCurrentStep('preview');

      // Pre-fill the discount input from the first preview (carry-forward), then watch for
      // silent drops on later previews (a carried code re-validation-failed with no error)
      const appliedCode = extractAppliedDiscountCode(data.fee_breakdown?.fee_collections);
      if (!discountInitialized.current) {
        setDiscountCode(appliedCode);
        setInitialDiscountCode(appliedCode);
        discountInitialized.current = true;
        setDiscountSilentDrop(false);
      } else {
        const enteredCode = discountCode.trim();
        setDiscountSilentDrop(Boolean(enteredCode) && !appliedCode && !data.discount_error);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load modification preview');
    } finally {
      setLoading(false);
    }
  };

  // Guest names live directly on the reservation row, so this edit goes through the
  // plain reservation update endpoint — no pricing, versioning, or payment involved.
  const submitGuestChange = async () => {
    if (!reservation) return;

    setLoading(true);
    setError(null);

    try {
      await managerApi.put(`/reservations/${reservation.id}`, {
        guest_first_name: guestFirstName.trim(),
        guest_last_name: guestLastName.trim(),
        other_guests: otherGuestsValue,
      });
      setGuestUpdateSuccess(true);
      setCurrentStep('result');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update guests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!reservation || !modificationType) return;

    if (isGuestEdit) {
      await submitGuestChange();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let endpoint: string;
      let body: Record<string, any>;

      // Only collect payment when the modification actually adds a charge.
      const hasAdditionalCharge = (preview?.cost_difference || 0) > 0;
      const paymentFields = hasAdditionalCharge
        ? paymentMethod === 'payment_link'
          ? { send_payment_link: true }
          : {
              payment_method: paymentMethod,
              payment_status: paymentStatus,
              ...(paymentMethod === 'card_terminal'
                ? { terminal_transaction_id: terminalTransactionId.trim() }
                : {}),
            }
        : {};

      const baseBody = {
        waive_fees: selectedWaiveFees,
        reason: reason.trim(),
        ...paymentFields,
        ...(discountCode.trim() !== initialDiscountCode
          ? { discount_code: discountCode.trim() }
          : {}),
        ...buildFeeAdjustmentPayload(feeAdjustment),
      };

      const occupancyPayload = {
        new_adult: totalAdults,
        new_child: totalChildren,
        new_pets: totalPets,
        new_vehicles: totalVehicles,
        site_occupancies: siteOccupancies,
      };

      switch (modificationType) {
        case 'occupancy':
          endpoint = `/reservations/${reservation.id}/modify`;
          body = { ...baseBody, ...occupancyPayload };
          break;
        case 'date':
          endpoint = `/reservations/${reservation.id}/modify`;
          body = {
            ...baseBody,
            new_check_in: newCheckIn,
            new_days: newDays,
            ...occupancyPayload,
          };
          break;
        case 'site':
          endpoint = `/reservations/${reservation.id}/site-change`;
          body = {
            ...baseBody,
            new_site_aliases: selectedSiteAliases,
            ...occupancyPayload,
            site_occupancies: siteOccupancies.map((s, i) => ({
              ...s,
              site_alias: selectedSiteAliases[i] || s.site_alias,
            })),
          };
          break;
        case 'rebook':
          endpoint = `/reservations/${reservation.id}/rebook`;
          body = {
            ...baseBody,
            new_site_aliases: selectedSiteAliases,
            new_check_in: newCheckIn,
            new_days: newDays,
            ...occupancyPayload,
            site_occupancies: siteOccupancies.map((s, i) => ({
              ...s,
              site_alias: selectedSiteAliases[i] || s.site_alias,
            })),
          };
          break;
        default:
          throw new Error('Invalid modification type');
      }

      const data = await managerApi.post(endpoint, body);
      setResult(data);
      setCurrentStep('result');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to modify reservation');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type: ModificationType) => {
    setModificationType(type);
    setCurrentStep('edit');
  };

  const handleClose = () => {
    setCurrentStep('type');
    setModificationType(null);
    setPreview(null);
    setResult(null);
    setSelectedWaiveFees([]);
    setReason('');
    setPaymentMethod('payment_link');
    setPaymentStatus('pending');
    setTerminalTransactionId('');
    setError(null);
    setFullReservation(null);
    setSiteOccupancies([]);
    setSelectedSiteAliases([]);
    setIsGroupEventReservation(false);
    setGuestFirstName('');
    setGuestLastName('');
    setOtherGuestNames([]);
    setInitialGuests({ first: '', last: '', others: '' });
    setGuestUpdateSuccess(false);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === 'edit') {
      setCurrentStep('type');
      setModificationType(null);
    } else if (currentStep === 'preview') {
      setCurrentStep('edit');
      setPreview(null);
    } else if (currentStep === 'confirm') {
      setCurrentStep(isGuestEdit ? 'edit' : 'preview');
    }
  };

  const handleNext = () => {
    if (currentStep === 'edit') {
      if (isGuestEdit) {
        setCurrentStep('confirm');
      } else {
        loadPreview();
      }
    } else if (currentStep === 'preview') {
      setCurrentStep('confirm');
    }
  };

  const updateOtherGuest = (index: number, value: string) => {
    setOtherGuestNames((prev) => prev.map((name, i) => (i === index ? value : name)));
  };

  const removeOtherGuest = (index: number) => {
    setOtherGuestNames((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleWaiveFee = (feeKey: string) => {
    setSelectedWaiveFees(prev =>
      prev.includes(feeKey)
        ? prev.filter(f => f !== feeKey)
        : [...prev, feeKey]
    );
  };

  const calculateFinalCharge = (): number => {
    if (!preview) return 0;

    // cost_difference already includes the modification fee (backend: cost_difference =
    // new_cost + modification_fee - old_cost), so don't add modification_fee again here.
    let charge = preview.cost_difference;

    // Subtract waived fees
    for (const fee of preview.waivable_fees || []) {
      const feeKey = fee.site_alias ? `${fee.fee_type}:${fee.site_alias}` : fee.fee_type;
      if (selectedWaiveFees.includes(feeKey)) {
        charge -= fee.amount;
      }
    }

    return Math.max(0, charge);
  };

  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {steps.slice(0, -1).map((step, index) => (
        <div
          key={step.key}
          className={`wizard-step ${index <= currentStepIndex ? 'active' : ''} ${
            index < currentStepIndex ? 'completed' : ''
          }`}
        >
          <span className="step-dot">{index < currentStepIndex ? '✓' : index + 1}</span>
          <span className="step-label">{step.label}</span>
        </div>
      ))}
    </div>
  );

  const renderTypeStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Select Modification Type</h3>
      {loadingFullReservation && (
        <div className="loading-message">Loading reservation details...</div>
      )}
      <div className="modification-types">
        {MODIFICATION_TYPES.map((type) => (
          <button
            key={type.key}
            className="modification-type-option"
            onClick={() => handleSelectType(type.key)}
            disabled={loadingFullReservation}
          >
            <span className="type-label">{type.label}</span>
            <span className="type-description">{type.description}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderGuestsEditStep = () => {
    const namedCount = otherGuestNames.filter((n) => n.trim()).length + 1;
    const partySize = totalAdults + totalChildren;

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">
          {MODIFICATION_TYPES.find((t) => t.key === 'guests')?.label}
        </h3>

        <div className="current-values">
          <h4>Current Guests</h4>
          <p>
            Primary: {initialGuests.first || initialGuests.last
              ? `${initialGuests.first} ${initialGuests.last}`.trim()
              : reservation?.guestName || '(not set)'}
          </p>
          <p>Others: {initialGuests.others || '(none)'}</p>
        </div>

        <div className="edit-section">
          <h4>Primary Guest</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={guestFirstName}
                onChange={(e) => setGuestFirstName(e.target.value)}
                className="form-input"
                placeholder={reservation?.guestName || 'First name'}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={guestLastName}
                onChange={(e) => setGuestLastName(e.target.value)}
                className="form-input"
                placeholder="Last name"
              />
            </div>
          </div>
          <p className="guest-hint">
            The account holder ({reservation?.guestName}) is not changed here — only the name
            recorded on this reservation.
          </p>
        </div>

        <div className="edit-section">
          <h4>Additional Guests</h4>
          {otherGuestNames.length === 0 && (
            <p className="guest-hint">No additional guests on this reservation.</p>
          )}
          {otherGuestNames.map((name, index) => (
            <div key={index} className="guest-name-row">
              <input
                type="text"
                value={name}
                onChange={(e) => updateOtherGuest(index, e.target.value)}
                className="form-input"
                placeholder="Guest name"
              />
              <button
                type="button"
                className="btn btn-secondary guest-remove-btn"
                onClick={() => removeOtherGuest(index)}
              >
                REMOVE
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary guest-add-btn"
            onClick={() => setOtherGuestNames((prev) => [...prev, ''])}
          >
            + ADD GUEST
          </button>

          {partySize > 0 && namedCount > partySize && (
            <div className="guest-warning">
              {namedCount} names listed for an occupancy of {partySize} ({totalAdults} adults,{' '}
              {totalChildren} children). Use "A: Quick Occupancy" if the headcount changed too.
            </div>
          )}
          {guestsTooLong && (
            <div className="error-message">
              Guest list is {otherGuestsValue.length} characters; the limit is{' '}
              {OTHER_GUESTS_MAX_LENGTH}. Shorten or remove a name.
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const renderEditStep = () => {
    if (loadingFullReservation) {
      return (
        <div className="wizard-content">
          <div className="loading-message">Loading reservation details...</div>
        </div>
      );
    }

    if (isGuestEdit) {
      return renderGuestsEditStep();
    }

    const showDateFields = modificationType === 'date' || modificationType === 'rebook';
    const showSiteFields = modificationType === 'site' || modificationType === 'rebook';
    const siteCount = siteOccupancies.length;

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">
          {MODIFICATION_TYPES.find(t => t.key === modificationType)?.label}
        </h3>

        <div className="current-values">
          <h4>Current Reservation ({siteCount} site{siteCount !== 1 ? 's' : ''})</h4>
          <p>Dates: {newCheckIn} ({fullReservation?.days} nights)</p>
          {siteOccupancies.map((site, idx) => (
            <p key={idx}>
              Site {site.site_alias}: {site.adults}A, {site.children}C, {site.pets}P, {site.vehicles}V
            </p>
          ))}
        </div>

        {showDateFields && (
          <div className="edit-section">
            <h4>Dates</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>New Check-in Date</label>
                <input
                  type="date"
                  value={newCheckIn}
                  onChange={(e) => setNewCheckIn(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Number of Nights</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={newDays}
                  onChange={(e) => setNewDays(parseInt(e.target.value) || 1)}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        )}

        {showSiteFields && (
          <div className="edit-section">
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={isGroupEventReservation}
                onChange={(e) => setIsGroupEventReservation(e.target.checked)}
              />
              <span>Is this reservation part of an event/group?</span>
            </label>
            {isGroupEventReservation && (
              <div className="warning-message" style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-md)', background: 'rgba(245, 158, 11, 0.1)', borderRadius: 'var(--radius-sm)', color: 'var(--status-pending)' }}>
                Please remove the target site from the event to unlock it first!
              </div>
            )}
          </div>
        )}

        <div className="edit-section">
          <h4>Occupancy by Site</h4>
          {siteOccupancies.map((site, index) => (
            <div key={index} className="site-occupancy-card">
              <div className="site-occupancy-card-header">
                <span className="site-badge">Site {index + 1}</span>
                <strong>{showSiteFields ? (selectedSiteAliases[index] || site.site_alias) : site.site_alias}</strong>
              </div>

              {showSiteFields && (
                <div className="form-group">
                  <label>Site</label>
                  <select
                    value={selectedSiteAliases[index] || site.site_alias}
                    onChange={(e) => {
                      const newAliases = [...selectedSiteAliases];
                      newAliases[index] = e.target.value;
                      setSelectedSiteAliases(newAliases);
                    }}
                    className="form-input"
                  >
                    <option value={site.site_alias}>
                      {site.site_alias} (original)
                    </option>
                    {availableSites
                      .filter((s: any) => s.alias !== site.site_alias && !s.seasonal) // Exclude current site and seasonal sites
                      .map((s: any) => (
                        <option key={s.alias} value={s.alias}>
                          {s.alias} - {s.site_type || s.type || s.name || ''} (${s.price_per_night}/night)
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="form-grid occupancy-grid">
                <div className="form-group">
                  <label>Adults</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={site.adults}
                    onChange={(e) => updateSiteOccupancy(index, 'adults', parseInt(e.target.value) || 1)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Children</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={site.children}
                    onChange={(e) => updateSiteOccupancy(index, 'children', parseInt(e.target.value) || 0)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Pets</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={site.pets}
                    onChange={(e) => updateSiteOccupancy(index, 'pets', parseInt(e.target.value) || 0)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Vehicles</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={site.vehicles}
                    onChange={(e) => updateSiteOccupancy(index, 'vehicles', parseInt(e.target.value) || 0)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          ))}

          {siteCount > 1 && (
            <div className="occupancy-totals">
              <strong>Totals:</strong> {totalAdults} adults, {totalChildren} children
              {totalPets > 0 && `, ${totalPets} pets`}
              {totalVehicles > 0 && `, ${totalVehicles} vehicles`}
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const renderPreviewStep = () => {
    const siteDetails = preview?.fee_breakdown?.site_details;
    const isMultiSite = siteDetails && siteDetails.length > 1;
    const currentSiteAliases = preview?.current.sites?.map(s => s.site_alias).join(', ');
    const proposedSiteAliases = preview?.proposed.sites?.map(s => s.site_alias).join(', ');

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">Preview Changes</h3>

        {preview && (
          <>
            <div className="comparison-table">
              <div className="comparison-header">
                <span></span>
                <span>CURRENT</span>
                <span>PROPOSED</span>
              </div>
              {(currentSiteAliases || proposedSiteAliases) && (
                <div className="comparison-row">
                  <span>Sites:</span>
                  <span>{currentSiteAliases || reservation?.siteAlias}</span>
                  <span className={currentSiteAliases !== proposedSiteAliases ? 'changed' : ''}>
                    {proposedSiteAliases || selectedSiteAliases.join(', ')}
                  </span>
                </div>
              )}
              <div className="comparison-row">
                <span>Check-in:</span>
                <span>{preview.current.check_in}</span>
                <span className={preview.current.check_in !== preview.proposed.check_in ? 'changed' : ''}>
                  {preview.proposed.check_in}
                </span>
              </div>
              <div className="comparison-row">
                <span>Nights:</span>
                <span>{preview.current.days}</span>
                <span className={preview.current.days !== preview.proposed.days ? 'changed' : ''}>
                  {preview.proposed.days}
                </span>
              </div>
              <div className="comparison-row">
                <span>Adults:</span>
                <span>{preview.current.adult}</span>
                <span className={preview.current.adult !== preview.proposed.adult ? 'changed' : ''}>
                  {preview.proposed.adult}
                </span>
              </div>
              <div className="comparison-row">
                <span>Children:</span>
                <span>{preview.current.child}</span>
                <span className={preview.current.child !== preview.proposed.child ? 'changed' : ''}>
                  {preview.proposed.child}
                </span>
              </div>
              <div className="comparison-row">
                <span>Cost:</span>
                <span>${preview.current.cost.toFixed(2)}</span>
                <span className={preview.current.cost !== preview.proposed.cost ? 'changed' : ''}>
                  ${preview.proposed.cost.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Per-site cost breakdown for multi-site reservations */}
            {isMultiSite && siteDetails && (
              <div className="per-site-breakdown">
                <h4>Per-Site Cost Breakdown</h4>
                <div className="per-site-cost-table">
                  <div className="per-site-cost-header">
                    <span>Site</span>
                    <span>Base Cost</span>
                    <span>Fees</span>
                    <span>Site Total</span>
                  </div>
                  {siteDetails.map((detail) => {
                    const additionalFees =
                      (detail.occupancy_fees?.reduce((s, f) => s + f.amount, 0) || 0) +
                      (detail.pet_fees?.reduce((s, f) => s + f.amount, 0) || 0) +
                      (detail.vehicle_fees?.reduce((s, f) => s + f.amount, 0) || 0) +
                      (detail.site_imposed_fees?.reduce((s, f) => s + f.amount, 0) || 0);
                    return (
                      <div key={detail.site_alias} className="per-site-cost-row">
                        <span>{detail.site_alias}</span>
                        <span>${detail.base_cost.toFixed(2)}</span>
                        <span>${additionalFees.toFixed(2)}</span>
                        <span>${detail.site_total.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="cost-breakdown">
              <h4>Cost Impact</h4>
              <div className="cost-row">
                <span>Current total</span>
                <span>${preview.current.cost.toFixed(2)}</span>
              </div>
              <div className="cost-row">
                <span>New total</span>
                <span>${preview.proposed.cost.toFixed(2)}</span>
              </div>
              <div className="cost-row">
                <span>Modification fee</span>
                <span>${preview.modification_fee.toFixed(2)}</span>
              </div>
              <div className="cost-row cost-total">
                <span>{preview.cost_difference >= 0 ? 'Additional Charge' : 'Refund'}</span>
                <span className={preview.cost_difference >= 0 ? 'charge' : 'refund'}>
                  ${Math.abs(preview.cost_difference).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Discount code + fee adjustment (recalculate the preview on blur) */}
            <div className="config-section">
              <h4>Discount &amp; Adjustments</h4>
              <DiscountCodeField
                value={discountCode}
                onChange={setDiscountCode}
                onApply={loadPreview}
                hasExisting={Boolean(initialDiscountCode)}
                error={preview.discount_error?.message}
                silentDropNotice={
                  discountSilentDrop ? 'Previous discount no longer applicable' : null
                }
                disabled={loading}
              />
              <FeeAdjustmentFields
                canApply={preview.admin_capabilities.can_apply_fee_adjustment ?? false}
                value={feeAdjustment}
                onChange={setFeeAdjustment}
                onBlur={loadPreview}
                disabled={loading}
              />
              {((preview.fee_breakdown?.fee_collections?.discount?.length || 0) > 0 ||
                (preview.fee_breakdown?.fee_collections?.fee_adjustment?.length || 0) > 0) && (
                <div className="cost-breakdown">
                  <AppliedFeeLines feeCollections={preview.fee_breakdown?.fee_collections} />
                </div>
              )}
            </div>

            {preview.admin_capabilities.can_waive_fees && preview.waivable_fees?.length > 0 && (
              <div className="config-section">
                <h4>Waive Fees</h4>
                {preview.waivable_fees.map((fee) => {
                  const feeKey = fee.site_alias ? `${fee.fee_type}:${fee.site_alias}` : fee.fee_type;
                  return (
                    <label key={feeKey} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={selectedWaiveFees.includes(feeKey)}
                        onChange={() => toggleWaiveFee(feeKey)}
                      />
                      <span>
                        Waive {fee.description} (${fee.amount.toFixed(2)})
                        {fee.site_alias && <span className="fee-site-label"> - Site {fee.site_alias}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedWaiveFees.length > 0 && (
              <div className="adjusted-total">
                <span>Adjusted Charge:</span>
                <span className="adjusted-amount">${calculateFinalCharge().toFixed(2)}</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderGuestsConfirmStep = () => {
    const newPrimary = `${guestFirstName.trim()} ${guestLastName.trim()}`.trim();
    const oldPrimary = `${initialGuests.first} ${initialGuests.last}`.trim();

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">Confirm Guest Change</h3>

        <div className="comparison-table">
          <div className="comparison-header">
            <span></span>
            <span>CURRENT</span>
            <span>PROPOSED</span>
          </div>
          <div className="comparison-row">
            <span>Primary:</span>
            <span>{oldPrimary || '(not set)'}</span>
            <span className={oldPrimary !== newPrimary ? 'changed' : ''}>
              {newPrimary || '(not set)'}
            </span>
          </div>
          <div className="comparison-row">
            <span>Others:</span>
            <span>{initialGuests.others || '(none)'}</span>
            <span className={initialGuests.others !== otherGuestsValue ? 'changed' : ''}>
              {otherGuestsValue || '(none)'}
            </span>
          </div>
        </div>

        <div className="confirm-summary">
          <div className="confirm-row">
            <span>Sites:</span>
            <span>{siteOccupancies.map((s) => s.site_alias).join(', ')}</span>
          </div>
          <div className="confirm-row">
            <span>Occupancy:</span>
            <span>
              {totalAdults} adults, {totalChildren} children (unchanged)
            </span>
          </div>
          <div className="confirm-row">
            <span>Amount Due:</span>
            <span className="highlight">$0.00</span>
          </div>
        </div>

        <p className="guest-hint">
          Names only — dates, sites, occupancy, and cost stay as they are, and no email is sent to
          the guest.
        </p>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const renderConfirmStep = () => isGuestEdit ? renderGuestsConfirmStep() : (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Confirm Modification</h3>

      <div className="confirm-summary">
        <div className="confirm-row">
          <span>Modification Type:</span>
          <span>{MODIFICATION_TYPES.find(t => t.key === modificationType)?.label}</span>
        </div>
        <div className="confirm-row">
          <span>Sites:</span>
          <span>{siteOccupancies.length} ({selectedSiteAliases.join(', ')})</span>
        </div>
        <div className="confirm-row">
          <span>Amount Due:</span>
          <span className="highlight">${calculateFinalCharge().toFixed(2)}</span>
        </div>
        {selectedWaiveFees.length > 0 && (
          <div className="confirm-row">
            <span>Fees Waived:</span>
            <span>{selectedWaiveFees.join(', ')}</span>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Modification Reason <span className="required">*</span></label>
        <textarea
          className="form-textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for modification (required for audit trail)..."
          rows={4}
        />
      </div>

      {calculateFinalCharge() > 0 && (
        <div className="payment-options">
          <h4>Payment Collection</h4>
          <div className="payment-methods">
            <label className="payment-method-option">
              <input
                type="radio"
                name="modifyPaymentMethod"
                value="cash"
                checked={paymentMethod === 'cash'}
                onChange={() => { setPaymentMethod('cash'); setPaymentStatus('paid'); }}
              />
              <span>Cash</span>
            </label>
            <label className="payment-method-option">
              <input
                type="radio"
                name="modifyPaymentMethod"
                value="check"
                checked={paymentMethod === 'check'}
                onChange={() => { setPaymentMethod('check'); setPaymentStatus('paid'); }}
              />
              <span>Check</span>
            </label>
            <label className="payment-method-option">
              <input
                type="radio"
                name="modifyPaymentMethod"
                value="card_terminal"
                checked={paymentMethod === 'card_terminal'}
                onChange={() => { setPaymentMethod('card_terminal'); setPaymentStatus('paid'); }}
              />
              <span>Card Terminal</span>
            </label>
            <label className="payment-method-option">
              <input
                type="radio"
                name="modifyPaymentMethod"
                value="payment_link"
                checked={paymentMethod === 'payment_link'}
                onChange={() => { setPaymentMethod('payment_link'); setPaymentStatus('pending'); }}
              />
              <span>Send Payment Link</span>
            </label>
          </div>

          <div className="payment-method-fields">
            {(paymentMethod === 'cash' || paymentMethod === 'check') && (
              <div className="form-group">
                <label>Payment Status</label>
                <select
                  className="form-input"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as 'paid' | 'pending')}
                >
                  <option value="paid">Paid Now</option>
                  <option value="pending">Pay at Check-in</option>
                </select>
              </div>
            )}

            {paymentMethod === 'card_terminal' && (
              <div className="form-group">
                <label>Transaction ID <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={terminalTransactionId}
                  onChange={(e) => setTerminalTransactionId(e.target.value)}
                  placeholder="Enter transaction ID from terminal receipt"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  const renderGuestsResultStep = () => (
    <div className="wizard-content result-content">
      <div className="result-icon success">✓</div>
      <h3 className="result-title">Guests Updated!</h3>
      <div className="result-details">
        <p className="confirmation-code">
          Confirmation: <strong>{reservation?.confirmationCode}</strong>
        </p>
        <p>
          Primary: {`${guestFirstName.trim()} ${guestLastName.trim()}`.trim() || '(not set)'}
        </p>
        <p>Others: {otherGuestsValue || '(none)'}</p>
      </div>
    </div>
  );

  const renderResultStep = () => isGuestEdit && guestUpdateSuccess ? renderGuestsResultStep() : (
    <div className="wizard-content result-content">
      {result?.success ? (
        <>
          <div className="result-icon success">✓</div>
          <h3 className="result-title">Reservation Modified!</h3>
          <div className="result-details">
            <p className="confirmation-code">
              Confirmation: <strong>{result.confirmation_code}</strong>
            </p>
            <p>Sites: {selectedSiteAliases.join(', ')}</p>
            <p>New check-in: {preview?.proposed.check_in}</p>
            <p>Duration: {preview?.proposed.days} nights</p>
            <p>Cost: ${result.cost_details.new_cost.toFixed(2)}</p>
            {result.cost_details.additional_charge > 0 && (
              <p>
                Additional charge: ${result.cost_details.additional_charge.toFixed(2)}
                {result.payment_link_sent
                  ? ' (payment link sent)'
                  : paymentMethod !== 'payment_link' &&
                    ` (${paymentMethod.replace('_', ' ')}, ${
                      paymentStatus === 'paid' ? 'paid' : 'pay at check-in'
                    })`}
              </p>
            )}
            {result.cost_details.refund_amount > 0 && (
              <p>Refund: ${result.cost_details.refund_amount.toFixed(2)}</p>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="result-icon error">✗</div>
          <h3 className="result-title">Modification Failed</h3>
          <div className="result-details">
            <p className="error-message">{error || 'An unknown error occurred'}</p>
          </div>
        </>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'type':
        return renderTypeStep();
      case 'edit':
        return renderEditStep();
      case 'preview':
        return renderPreviewStep();
      case 'confirm':
        return renderConfirmStep();
      case 'result':
        return renderResultStep();
      default:
        return null;
    }
  };

  const footer = currentStep === 'result' ? (
    <button className="btn btn-primary" onClick={handleClose}>
      DONE
    </button>
  ) : currentStep === 'type' ? (
    <button className="btn btn-secondary" onClick={handleClose}>
      CANCEL
    </button>
  ) : (
    <>
      <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
        CANCEL
      </button>
      <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>
        ← BACK
      </button>
      {currentStep === 'confirm' ? (
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            (isGuestEdit
              ? !guestsChanged || guestsTooLong
              : !reason.trim() ||
                (calculateFinalCharge() > 0 &&
                  paymentMethod === 'card_terminal' &&
                  !terminalTransactionId.trim()))
          }
        >
          {loading ? 'PROCESSING...' : isGuestEdit ? 'SAVE GUESTS' : 'APPLY MODIFICATION'}
        </button>
      ) : (
        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={loading || (isGuestEdit && currentStep === 'edit' && (!guestsChanged || guestsTooLong))}
        >
          {loading ? 'LOADING...' : 'NEXT →'}
        </button>
      )}
    </>
  );

  if (!reservation) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Modify Reservation ${reservation.confirmationCode} - Step ${currentStepIndex + 1}/${steps.length}`}
      size="large"
      footer={footer}
    >
      <div className="modify-wizard">
        {currentStep !== 'result' && renderStepIndicator()}
        {renderCurrentStep()}
      </div>
    </Modal>
  );
};

export default ModificationWizard;
